import styled, { createGlobalStyle, css } from 'styled-components';

import { rem } from '../../tokens/spacing';
import { zIndex } from '../../tokens/zIndex';

/**
 * Calendar chrome lives **inside react-datepicker’s DOM** (`.react-datepicker__*`), which this
 * package does not render as styled-components. **`createGlobalStyle` scoped to `.wl-datepicker-ds`**
 * is the same pattern as app `GlobalStyles`: one global sheet, selectors narrowed so it cannot
 * restyle arbitrary pages. The **text field** stays in `styled.*` above; only the popper/grid uses this.
 *
 * **Production** density keeps **root `rem`** math (`1rem` calendar, `2.7rem` cells) so it tracks
 * `html { font-size: 10px }` like legacy `datePicker.styled.ts`.
 */
export const DatePickerCalendarGlobalStyles = createGlobalStyle`
  /* Time column width: must match next-arrow offset when calendar + time (library uses 85px). */
  .wl-datepicker-ds {
    --wl-datepicker-time-col: ${rem(100)};
  }

  .wl-datepicker-ds[data-calendar-density='relaxed'] {
    --wl-datepicker-time-col: ${rem(110)};
  }

  .wl-datepicker-ds .react-datepicker__triangle {
    display: none;
  }

  .wl-datepicker-ds .react-datepicker-wrapper,
  .wl-datepicker-ds .wl-datepicker-ds-react-datepicker-wrapper.react-datepicker-wrapper {
    z-index: 1;
    width: 100%;
    display: inline-block;
  }

  .wl-datepicker-ds .react-datepicker-popper {
    z-index: ${zIndex.datepicker};
  }

  .wl-datepicker-ds .react-datepicker__time-container
    .react-datepicker__time
    .react-datepicker__time-box
    ul.react-datepicker__time-list
    li.react-datepicker__time-list-item--selected {
    background-color: var(--ui-interactive, #7b53d1);
  }

  .wl-datepicker-ds
    .react-datepicker__time-container
    .react-datepicker__time
    .react-datepicker__time-box
    ul.react-datepicker__time-list
    li.react-datepicker__time-list-item--selected:hover {
    background-color: var(--ui-interactive, #7b53d1);
  }

  .wl-datepicker-ds .react-datepicker__day--outside-month {
    color: var(--ui-text-disabled, #d7d7d7);
    cursor: default !important;
  }

  .wl-datepicker-ds .react-datepicker__day--outside-month:hover {
    background: transparent;
  }

  .wl-datepicker-ds .react-datepicker__day {
    border: ${rem(1)} solid transparent;
  }

  .wl-datepicker-ds .react-datepicker__day--selected,
  .wl-datepicker-ds .react-datepicker__day--in-selecting-range,
  .wl-datepicker-ds .react-datepicker__day--in-range,
  .wl-datepicker-ds .react-datepicker__month-text--selected,
  .wl-datepicker-ds .react-datepicker__month-text--in-selecting-range,
  .wl-datepicker-ds .react-datepicker__month-text--in-range,
  .wl-datepicker-ds .react-datepicker__quarter-text--selected,
  .wl-datepicker-ds .react-datepicker__quarter-text--in-selecting-range,
  .wl-datepicker-ds .react-datepicker__quarter-text--in-range,
  .wl-datepicker-ds .react-datepicker__year-text--selected,
  .wl-datepicker-ds .react-datepicker__year-text--in-selecting-range,
  .wl-datepicker-ds .react-datepicker__year-text--in-range,
  .wl-datepicker-ds .react-datepicker__day--keyboard-selected,
  .wl-datepicker-ds .react-datepicker__month-text--keyboard-selected,
  .wl-datepicker-ds .react-datepicker__quarter-text--keyboard-selected,
  .wl-datepicker-ds .react-datepicker__year-text--keyboard-selected {
    background-color: var(--ui-interactive, #7b53d1);
    outline: var(--ui-interactive, #7b53d1);
    border-radius: var(--ui-radius-sm, ${rem(5)});
    color: var(--ui-on-interactive, #fff);
  }

  .wl-datepicker-ds .react-datepicker__current-month,
  .wl-datepicker-ds .react-datepicker-time__header,
  .wl-datepicker-ds .react-datepicker-year-header,
  .wl-datepicker-ds .react-datepicker__day-names div,
  .wl-datepicker-ds .react-datepicker__year-read-view,
  .wl-datepicker-ds .react-datepicker__month-read-view,
  .wl-datepicker-ds .react-datepicker__month-year-read-view {
    color: var(--ui-on-interactive, #fff);
  }

  .wl-datepicker-ds .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle {
    border-bottom-color: var(--ui-interactive, #7b53d1);
  }

  .wl-datepicker-ds .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle::before {
    border-top-color: var(--ui-border-muted, #ededed);
  }

  .wl-datepicker-ds .react-datepicker__navigation--previous {
    border-right-color: var(--ui-on-interactive, #ffffff);
  }

  .wl-datepicker-ds .react-datepicker__navigation--next {
    border-left-color: var(--ui-on-interactive, #ffffff);
  }

  /* Keep next-arrow inset from time column when showTimeSelect (library default 85px; we use --wl-datepicker-time-col). */
  .wl-datepicker-ds
    .react-datepicker__navigation--next:not(.react-datepicker__navigation--next--with-time):not(.react-datepicker__navigation--next--with-today-button) {
    right: ${rem(2)};
  }

  .wl-datepicker-ds
    .react-datepicker__navigation--next.react-datepicker__navigation--next--with-time:not(.react-datepicker__navigation--next--with-today-button) {
    /* Slight inset left of the time column so the chevron does not hug the divider. */
    right: calc(var(--wl-datepicker-time-col) + ${rem(4)});
  }

  .wl-datepicker-ds .react-datepicker__year-read-view--down-arrow,
  .wl-datepicker-ds .react-datepicker__month-read-view--down-arrow,
  .wl-datepicker-ds .react-datepicker__month-year-read-view--down-arrow,
  .wl-datepicker-ds .react-datepicker__year-read-view:hover .react-datepicker__year-read-view--down-arrow,
  .wl-datepicker-ds .react-datepicker__year-read-view:hover .react-datepicker__month-read-view--down-arrow,
  .wl-datepicker-ds .react-datepicker__month-read-view:hover .react-datepicker__year-read-view--down-arrow,
  .wl-datepicker-ds .react-datepicker__month-read-view:hover .react-datepicker__month-read-view--down-arrow,
  .wl-datepicker-ds .react-datepicker__month-year-read-view:hover .react-datepicker__year-read-view--down-arrow,
  .wl-datepicker-ds .react-datepicker__month-year-read-view:hover .react-datepicker__month-read-view--down-arrow {
    border-top-color: var(--ui-on-interactive, #fff);
    border-width: ${rem(4)};
  }

  .wl-datepicker-ds .react-datepicker__header {
    background: var(--ui-interactive, #7b53d1);
    border: none;
  }

  /* Month/year: header has no day-name row — center year (and peers) vertically in the purple band. */
  .wl-datepicker-ds .react-datepicker__month-container > .react-datepicker__header:not(:has(.react-datepicker__day-names)) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: ${rem(48)};
    box-sizing: border-box;
  }

  .wl-datepicker-ds .react-datepicker__time-container,
  .wl-datepicker-ds .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box {
    width: var(--wl-datepicker-time-col);
  }

  /* Time caption sits in a narrow column — keep on one line (density blocks set font-size). */
  .wl-datepicker-ds .react-datepicker-time__header {
    overflow: visible;
    text-overflow: clip;
    white-space: nowrap;
    text-align: center;
    padding-left: ${rem(4)};
    padding-right: ${rem(4)};
    font-weight: 600;
  }

  .wl-datepicker-ds .react-datepicker__day--keyboard-selected:focus-visible,
  .wl-datepicker-ds .react-datepicker__navigation--previous:focus-visible,
  .wl-datepicker-ds .react-datepicker__navigation--next:focus-visible,
  .wl-datepicker-ds .react-datepicker__day--selected:focus-within {
    outline: ${rem(1)} solid var(--ui-text, #161616) !important;
    border: ${rem(1)} solid var(--ui-text, #161616) !important;
    border-radius: var(--ui-radius-sm, ${rem(5)});
  }

  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker {
    font-family: var(--ui-font-sans, 'HK Grotesk', 'Helvetica Neue', helvetica, arial, sans-serif);
    font-size: ${rem(10)};
    border-radius: var(--ui-radius-md, ${rem(6)});
    border: ${rem(1)} solid var(--ui-border-muted, #ededed);
    background-color: var(--ui-surface, #fff);
    color: var(--ui-text, #000);
    box-shadow: none;
  }

  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker-popper[data-placement^='bottom'] {
    padding-top: ${rem(8)} !important;
  }

  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker-popper[data-placement^='top'] {
    padding-bottom: ${rem(8)} !important;
  }

  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__header {
    padding: ${rem(8)} 0;
  }

  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__navigation {
    height: ${rem(32)};
    width: ${rem(32)};
    top: ${rem(2)};
  }

  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__navigation--previous {
    left: ${rem(2)};
  }

  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__month {
    margin: ${rem(4)};
  }

  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__day-names {
    margin-bottom: 0;
  }

  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__day-name,
  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__day,
  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__time-name {
    width: ${rem(27)};
    line-height: ${rem(27)};
    margin: 0;
    vertical-align: top;
    box-sizing: border-box;
  }

  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker-year-header,
  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__current-month {
    font-size: ${rem(15)};
    line-height: 1.25;
    padding: 0 ${rem(36)};
  }

  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker-time__header {
    font-size: ${rem(11)};
    line-height: 1.2;
    padding: 0 ${rem(6)};
  }

  /* Month/year only: vertically center nav with the shorter header (day+names keeps top: rem(2)). */
  .wl-datepicker-ds[data-calendar-density='production']
    .react-datepicker:has(.react-datepicker__month-container > .react-datepicker__header:not(:has(.react-datepicker__day-names)))
    .react-datepicker__navigation {
    top: 50%;
    transform: translateY(-50%);
  }

  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__year-read-view--down-arrow,
  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__month-read-view--down-arrow,
  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__month-year-read-view--down-arrow,
  .wl-datepicker-ds[data-calendar-density='production'] .react-datepicker__navigation-icon::before {
    border-color: var(--ui-border, #ccc);
    border-style: solid;
    border-width: ${rem(3)} ${rem(3)} 0 0;
    content: '';
    display: block;
    height: ${rem(9)};
    position: absolute;
    width: ${rem(9)};
    top: ${rem(12)};
  }

  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker {
    font-family: var(--ui-font-sans, 'HK Grotesk', 'Helvetica Neue', helvetica, arial, sans-serif);
    font-size: ${rem(11)};
    border-radius: var(--ui-radius-md, ${rem(6.5)});
    border: ${rem(1)} solid var(--ui-border-muted, #ededed);
    background-color: var(--ui-surface, #fff);
    color: var(--ui-text, #000);
    box-shadow: 0 ${rem(2)} ${rem(14)} var(--ui-shadow-color, rgba(0, 0, 0, 0.07));
  }

  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker-popper[data-placement^='bottom'] {
    padding-top: ${rem(10)} !important;
  }

  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker-popper[data-placement^='top'] {
    padding-bottom: ${rem(10)} !important;
  }

  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker__header {
    padding: ${rem(10)} 0 ${rem(8)};
  }

  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker__navigation {
    height: ${rem(36)};
    width: ${rem(36)};
    top: ${rem(4)};
  }

  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker__month {
    margin: ${rem(4.5)};
  }

  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker__day-name,
  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker__day,
  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker__time-name {
    width: ${rem(30)};
    line-height: ${rem(30)};
    margin: 0;
    vertical-align: top;
    box-sizing: border-box;
  }

  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker-year-header,
  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker__current-month {
    font-size: ${rem(16.5)};
    line-height: 1.2;
    padding: 0 ${rem(40)};
  }

  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker-time__header {
    font-size: ${rem(13)};
    line-height: 1.2;
    padding: 0 ${rem(6)};
  }

  .wl-datepicker-ds[data-calendar-density='relaxed']
    .react-datepicker:has(.react-datepicker__month-container > .react-datepicker__header:not(:has(.react-datepicker__day-names)))
    .react-datepicker__navigation {
    top: 50%;
    transform: translateY(-50%);
  }

  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker__year-read-view--down-arrow,
  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker__month-read-view--down-arrow,
  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker__month-year-read-view--down-arrow,
  .wl-datepicker-ds[data-calendar-density='relaxed'] .react-datepicker__navigation-icon::before {
    border-color: var(--ui-border, #ccc);
    border-style: solid;
    border-width: ${rem(3)} ${rem(3)} 0 0;
    content: '';
    display: block;
    height: ${rem(9)};
    position: absolute;
    width: ${rem(9)};
    top: ${rem(13)};
  }
`;

export const CalendarTrigger = styled.button`
  position: absolute;
  top: 50%;
  right: ${rem(6)};
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(36)};
  height: ${rem(36)};
  padding: 0;
  margin: 0;
  border: none;
  border-radius: var(--ui-radius-sm, ${rem(4)});
  background: transparent;
  color: var(--ui-text-subtle);
  cursor: pointer;

  &:hover:not(:disabled) {
    color: var(--ui-interactive);
  }

  &:focus-visible {
    outline: ${rem(2)} solid var(--ui-input-border-focus, var(--ui-interactive));
    outline-offset: ${rem(1)};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .material-symbols-outlined {
    font-size: ${rem(20)};
    pointer-events: none;
  }
`;

export const ControlShell = styled.div`
  position: relative;
  width: 100%;

  &:focus-within ${CalendarTrigger} {
    color: var(--ui-input-border-focus, var(--ui-interactive));
  }
`;

export const StyledInput = styled.input<{ $disabled?: boolean; $invalid?: boolean }>`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: ${rem(11)} ${rem(12)};
  padding-right: ${rem(44)};
  border: ${rem(1)} solid var(--ui-input-border, var(--ui-border));
  border-radius: var(--ui-radius-md, ${rem(6)});
  background: var(--ui-surface);
  color: var(--ui-text);
  font-weight: 400;
  font-size: ${rem(15)};
  line-height: ${rem(16)};
  font-family: var(--ui-font-sans);
  -webkit-appearance: none;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.85 : 1)};

  &:hover:not(:disabled),
  &:focus {
    border: ${rem(1)} solid var(--ui-input-border-focus, var(--ui-interactive));
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    background-color: var(--ui-background-muted);
    border: ${rem(1)} solid var(--ui-border);
  }

  &::placeholder {
    font-weight: 400;
    color: var(--ui-text-subtle);
  }

  &:placeholder-shown {
    text-overflow: ellipsis;
  }

  ${({ $invalid }) =>
    $invalid &&
    css`
      &,
      &:hover,
      &:focus {
        border-color: var(--ui-status-danger, #d32f2f);
      }
    `}
`;

export const StyledRoot = styled.div`
  position: relative;
  display: block;
  width: 100%;
  max-width: 100%;
  font-size: ${rem(12)};
`;
